<template>
  <v-card outlined class="mb-4 license-card">
    <v-card-text>
      <v-row align="center" class="mb-3">
        <v-col cols="auto">
          <v-icon :color="iconColor" size="32">{{ sectionIcon }}</v-icon>
        </v-col>
        <v-col>
          <div class="text-h6 font-weight-bold" :class="`${iconColor}--text`">
            {{ sectionTitle }}
          </div>
        </v-col>
      </v-row>

      <v-row>
        <!-- เลขที่ใบอนุญาต -->
        <v-col cols="12" md="4">
          <v-text-field
            v-model="localData[licenseNumber]"
            outlined
            dense
            label="เลขที่ใบอนุญาต"
            prepend-inner-icon="mdi-card-text"
            placeholder="กรอกเลขที่ใบอนุญาต"
            hint="เลขที่ใบอนุญาตประกอบวิชาชีพ"
            persistent-hint
          >
            <template v-slot:append>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                    small
                    color="info"
                  >
                    mdi-information
                  </v-icon>
                </template>
                <span>เลขที่ปรากฏบนใบอนุญาตประกอบวิชาชีพ</span>
              </v-tooltip>
            </template>
          </v-text-field>
        </v-col>

        <!-- วันออกบัตร -->
        <v-col cols="12" md="4">
          <v-menu
            v-model="startDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="localData[startDate]"
                outlined
                dense
                label="วันที่ออกบัตร"
                prepend-inner-icon="mdi-calendar-start"
                readonly
                v-bind="attrs"
                v-on="on"
                :rules="startDateRules"
                placeholder="เลือกวันที่ออกบัตร"
                hint="วันที่ออกใบอนุญาต"
                persistent-hint
              >
                <template v-slot:append>
                  <v-icon color="primary">mdi-calendar</v-icon>
                </template>
              </v-text-field>
            </template>
            <v-date-picker
              v-model="localData[startDate]"
              no-title
              scrollable
              locale="th"
              :max="currentDate"
              @input="startDateMenu = false"
            >
              <v-spacer />
              <v-btn text color="primary" @click="startDateMenu = false">
                ปิด
              </v-btn>
            </v-date-picker>
          </v-menu>
        </v-col>

        <!-- วันหมดอายุ -->
        <v-col cols="12" md="4">
          <v-menu
            v-model="endDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="localData[endDate]"
                outlined
                dense
                label="วันที่หมดอายุ"
                prepend-inner-icon="mdi-calendar-end"
                readonly
                v-bind="attrs"
                v-on="on"
                :rules="endDateRules"
                placeholder="เลือกวันที่หมดอายุ"
                :hint="expiryHint"
                :persistent-hint="true"
                :error="isExpired"
              >
                <template v-slot:append>
                  <v-icon :color="expiryStatusColor">
                    {{ expiryStatusIcon }}
                  </v-icon>
                </template>
              </v-text-field>
            </template>
            <v-date-picker
              v-model="localData[endDate]"
              no-title
              scrollable
              locale="th"
              :min="localData[startDate] || currentDate"
              @input="endDateMenu = false"
            >
              <v-spacer />
              <v-btn text color="primary" @click="endDateMenu = false">
                ปิด
              </v-btn>
            </v-date-picker>
          </v-menu>
        </v-col>

        <!-- ไฟล์ใบอนุญาต -->
        <v-col cols="12">
          <v-divider class="my-2" />
          
          <!-- แสดงไฟล์ที่มีอยู่แล้ว -->
          <div v-if="localData[fileField]" class="file-display-section">
            <v-alert
              dense
              outlined
              type="success"
              icon="mdi-file-check"
              class="mb-3"
            >
              <v-row align="center" dense>
                <v-col cols="12" sm="8">
                  <div class="font-weight-bold">ไฟล์ใบอนุญาตที่อัปโหลดแล้ว</div>
                  <div class="text-caption">
                    <v-icon small>mdi-file-pdf-box</v-icon>
                    {{ localData[fileField] }}
                  </div>
                </v-col>
                <v-col cols="12" sm="4" class="text-right">
                  <v-btn
                    small
                    outlined
                    color="info"
                    class="mr-2"
                    @click="$emit('view')"
                  >
                    <v-icon small left>mdi-eye</v-icon>
                    ดูไฟล์
                  </v-btn>
                  <v-btn
                    small
                    outlined
                    color="error"
                    @click="confirmDelete"
                  >
                    <v-icon small left>mdi-delete</v-icon>
                    ลบ
                  </v-btn>
                </v-col>
              </v-row>
            </v-alert>
          </div>

          <!-- อัปโหลดไฟล์ใหม่ -->
          <div v-else class="file-upload-section">
            <v-file-input
              v-model="licenseFile"
              accept="application/pdf"
              outlined
              dense
              :label="fileLabel"
              prepend-icon="mdi-paperclip"
              prepend-inner-icon="mdi-file-pdf-box"
              :show-size="1000"
              :rules="fileRules"
              placeholder="เลือกไฟล์ PDF"
              hint="ขนาดไฟล์ไม่เกิน 5 MB, รองรับเฉพาะไฟล์ .pdf"
              persistent-hint
              @change="handleFileChange"
            >
              <template v-slot:selection="{ text }">
                <v-chip
                  small
                  color="primary"
                  text-color="white"
                  class="ma-1"
                >
                  <v-icon left small>mdi-file-pdf-box</v-icon>
                  {{ text }}
                </v-chip>
              </template>
            </v-file-input>

            <!-- คำแนะนำการอัปโหลด -->
            <v-expansion-panels flat class="mt-2">
              <v-expansion-panel>
                <v-expansion-panel-header color="grey lighten-4">
                  <div>
                    <v-icon small left color="info">mdi-information-outline</v-icon>
                    <span class="text-caption">คำแนะนำการอัปโหลดไฟล์</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon small color="success">mdi-check-circle</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="text-caption">
                          ไฟล์ต้องเป็นรูปแบบ PDF เท่านั้น
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon small color="success">mdi-check-circle</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="text-caption">
                          ขนาดไฟล์ไม่เกิน 5 MB
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon small color="success">mdi-check-circle</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="text-caption">
                          สแกนไฟล์ให้ชัดเจน อ่านง่าย
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon small color="success">mdi-check-circle</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="text-caption">
                          ตรวจสอบความถูกต้องของข้อมูลก่อนอัปโหลด
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "LicenseSection",
  
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    licenseType: {
      type: String,
      required: true,
      validator: value => ["teach", "director"].includes(value)
    }
  },

  data() {
    return {
      startDateMenu: false,
      endDateMenu: false,
      licenseFile: null,
      
      // กฎการตรวจสอบ
      licenseNumberRules: [
        v => (v && v.length >= 3) || "เลขที่ใบอนุญาตต้องมีอย่างน้อย 3 ตัวอักษร",
        v => (v && v.length <= 50) || "เลขที่ใบอนุญาตต้องไม่เกิน 50 ตัวอักษร"
      ],
      
      startDateRules: [
        v => this.validateStartDate(v) || "วันที่ออกบัตรต้องไม่เกินวันปัจจุบัน"
      ],
      
      endDateRules: [
        v => this.validateEndDate(v) || "วันที่หมดอายุต้องมากกว่าวันที่ออกบัตร"
      ],
      
      fileRules: [
        v => !v || v.size < 5000000 || "ขนาดไฟล์ต้องไม่เกิน 5 MB",
        v => !v || v.type === "application/pdf" || "รองรับเฉพาะไฟล์ PDF เท่านั้น"
      ]
    };
  },

  computed: {
    localData: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },

    // ชื่อฟิลด์ตาม licenseType
    licenseNumber() {
      return this.licenseType === "teach" ? "pt_licence" : "pt_licencedr";
    },

    startDate() {
      return this.licenseType === "teach" ? "pt_ondate" : "pt_ondatedr";
    },

    endDate() {
      return this.licenseType === "teach" ? "pt_enddate" : "pt_enddatedr";
    },

    fileField() {
      return this.licenseType === "teach" ? "pt_licence_file" : "pt_licencedr_file";
    },

    // UI Configuration
    sectionTitle() {
      return this.licenseType === "teach" 
        ? "ใบอนุญาตประกอบวิชาชีพครู"
        : "ใบอนุญาตประกอบวิชาชีพผู้บริหารสถานศึกษา";
    },

    sectionIcon() {
      return this.licenseType === "teach" 
        ? "mdi-book-education" 
        : "mdi-account-tie";
    },

    iconColor() {
      return this.licenseType === "teach" ? "primary" : "success";
    },

    fileLabel() {
      return this.licenseType === "teach" 
        ? "ใบอนุญาตประกอบวิชาชีพครู (PDF)"
        : "ใบอนุญาตประกอบวิชาชีพผู้บริหาร (PDF)";
    },

    // วันที่ปัจจุบัน
    currentDate() {
      return new Date().toISOString().substr(0, 10);
    },

    // ตรวจสอบสถานะหมดอายุ
    isExpired() {
      if (!this.localData[this.endDate]) return false;
      return new Date(this.localData[this.endDate]) < new Date();
    },

    isExpiringSoon() {
      if (!this.localData[this.endDate]) return false;
      const endDate = new Date(this.localData[this.endDate]);
      const today = new Date();
      const diffTime = endDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 && diffDays <= 90; // หมดอายุภายใน 90 วัน
    },

    expiryStatusColor() {
      if (this.isExpired) return "error";
      if (this.isExpiringSoon) return "warning";
      return "success";
    },

    expiryStatusIcon() {
      if (this.isExpired) return "mdi-alert-circle";
      if (this.isExpiringSoon) return "mdi-alert";
      return "mdi-check-circle";
    },

    expiryHint() {
      if (this.isExpired) return "⚠️ ใบอนุญาตหมดอายุแล้ว";
      if (this.isExpiringSoon) {
        const endDate = new Date(this.localData[this.endDate]);
        const today = new Date();
        const diffDays = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
        return `⚠️ ใบอนุญาตจะหมดอายุในอีก ${diffDays} วัน`;
      }
      return "ใบอนุญาตยังใช้งานได้";
    }
  },

  methods: {
    // ตรวจสอบวันที่เริ่มต้น
    validateStartDate(value) {
      if (!value) return true;
      return new Date(value) <= new Date();
    },

    // ตรวจสอบวันที่สิ้นสุด
    validateEndDate(value) {
      if (!value) return true;
      if (!this.localData[this.startDate]) return true;
      return new Date(value) > new Date(this.localData[this.startDate]);
    },

    // จัดการการเปลี่ยนแปลงไฟล์
    handleFileChange(file) {
      if (file) {
        // ตรวจสอบขนาดไฟล์
        if (file.size > 5000000) {
          this.$emit("file-error", "ขนาดไฟล์ต้องไม่เกิน 5 MB");
          this.licenseFile = null;
          return;
        }

        // ตรวจสอบประเภทไฟล์
        if (file.type !== "application/pdf") {
          this.$emit("file-error", "รองรับเฉพาะไฟล์ PDF เท่านั้น");
          this.licenseFile = null;
          return;
        }

        this.$emit("file-change", file);
      }
    },

    // ยืนยันการลบไฟล์
    async confirmDelete() {
      const result = await this.$swal({
        title: "ยืนยันการลบไฟล์",
        text: "คุณต้องการลบไฟล์ใบอนุญาตนี้หรือไม่?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "ใช่, ลบเลย",
        cancelButtonText: "ยกเลิก"
      });

      if (result.isConfirmed) {
        this.$emit("delete");
      }
    }
  }
};
</script>

<style scoped>
.license-card {
  transition: all 0.3s ease;
  border-radius: 12px;
}

.license-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.file-display-section,
.file-upload-section {
  padding: 8px 0;
}

/* Custom date picker styling */
::v-deep .v-text-field--outlined {
  border-radius: 8px;
}

::v-deep .v-input__icon--prepend-inner i {
  color: #1976d2 !important;
}

/* Alert styling */
::v-deep .v-alert {
  border-radius: 8px;
}

/* Expansion panel styling */
::v-deep .v-expansion-panel {
  border-radius: 8px !important;
}

::v-deep .v-expansion-panel-header {
  border-radius: 8px !important;
}

/* Chip styling */
::v-deep .v-chip {
  border-radius: 8px !important;
}
</style>